import { ExclamationCircleOutlined, MoreOutlined } from '@ant-design/icons';
import { Button, Card, Dropdown, Table, Tag, Tooltip } from 'antd';
import Column from 'antd/es/table/Column';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

import { useAuthContext } from 'hooks/useAuthContext';

import { KitCampaign } from 'models/KitCampaign';
import { Role } from 'models/Role';
import { TiendaPerfectaDistributor } from 'models/TiendaPerfecta';

import { UseRoleTableReturn } from '../hooks/useRoleTable';

interface RoleTableProps {
  table: UseRoleTableReturn;
  tableLoading: boolean;
}

const VISIBLE_CAMPAIGNS = 3;
const VISIBLE_DISTRIBUTORS = 3;

function RoleTable({ table, tableLoading }: RoleTableProps) {
  const { t } = useTranslation();
  const { user } = useAuthContext();
  const kitIntegrationEnabled = user?.company?.kitIntegrationEnabled ?? false;
  const tiendaPerfectaIntegrationEnabled =
    user?.company?.tiendaPerfectaIntegrationEnabled ?? false;
  const {
    filteredDataSource,
    tableParams,
    handleTableChange,
    getDropdownMenu,
  } = table;

  const renderDistributor = (distributor: TiendaPerfectaDistributor) =>
    distributor.active ? (
      <Tag key={distributor.id}>{distributor.tradeName}</Tag>
    ) : (
      <Tooltip
        key={distributor.id}
        title={t('scenes.settings.roles.scene.tooltips.inactive_distributor')}
      >
        <Tag color="red">
          {t('scenes.settings.roles.scene.table.inactive_distributor', {
            name: distributor.tradeName,
          })}
        </Tag>
      </Tooltip>
    );

  return (
    <Card style={{ marginTop: '12px' }}>
      <Table<Role>
        dataSource={filteredDataSource}
        bordered
        loading={tableLoading}
        size="small"
        scroll={{ x: 1300 }}
        pagination={tableParams.pagination}
        rowKey={(record) => record.id}
        onChange={handleTableChange}
      >
        <Column
          title={t('scenes.settings.roles.scene.table.columns.id')}
          dataIndex="id"
          key="id"
          width={60}
        />

        <Column
          title={t('scenes.settings.roles.scene.table.columns.name')}
          key="name"
          render={(row: Role) => (
            <span>
              {row.name}
              {row.isDefaultRoleForStartedAccount && (
                <Tooltip
                  title={t(
                    'scenes.settings.roles.scene.tooltips.default_role_indicator'
                  )}
                >
                  <Button
                    type="link"
                    shape="circle"
                    icon={<ExclamationCircleOutlined />}
                  />
                </Tooltip>
              )}
            </span>
          )}
        />

        {kitIntegrationEnabled && (
          <Column
            title={t('scenes.settings.roles.scene.table.columns.campaigns')}
            key="campaigns"
            render={(row: Role) => {
              if (row.kitCampaignsFullAccessEnabled) {
                return (
                  <Tag color="green">
                    {t(
                      'scenes.settings.roles.scene.table.campaigns_full_access'
                    )}
                  </Tag>
                );
              }
              const campaigns = row.campaigns ?? [];

              if (campaigns.length === 0) {
                return <span>-</span>;
              }

              const visible = campaigns.slice(0, VISIBLE_CAMPAIGNS);
              const extra = campaigns.slice(VISIBLE_CAMPAIGNS);
              return (
                <span style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                  {visible.map((c: KitCampaign) => (
                    <Tag key={c.id}>{c.name}</Tag>
                  ))}
                  {extra.length > 0 && (
                    <Tooltip
                      title={extra.map((c: KitCampaign) => c.name).join(', ')}
                    >
                      <Tag color="default" style={{ cursor: 'pointer' }}>
                        +{extra.length}
                      </Tag>
                    </Tooltip>
                  )}
                </span>
              );
            }}
          />
        )}

        {tiendaPerfectaIntegrationEnabled && (
          <Column
            title={t(
              'scenes.settings.roles.scene.table.columns.tienda_perfecta'
            )}
            key="tiendaPerfecta"
            width={320}
            render={(row: Role) => {
              // En tier `top` la API devuelve todos los distribuidores de la
              // company: listarlos contradice la idea de acceso total.
              if (row.tiendaPerfectaDistributorTier === 'top') {
                return (
                  <Tag color="green">
                    {t(
                      'scenes.settings.roles.scene.table.tienda_perfecta_full_access'
                    )}
                  </Tag>
                );
              }

              const distributors = row.tiendaPerfectaDistributors ?? [];

              if (distributors.length === 0) {
                return <span>-</span>;
              }

              const visible = distributors.slice(0, VISIBLE_DISTRIBUTORS);
              const extra = distributors.slice(VISIBLE_DISTRIBUTORS);

              return (
                <span style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                  {visible.map(renderDistributor)}
                  {extra.length > 0 && (
                    <Tooltip
                      title={extra
                        .map((d: TiendaPerfectaDistributor) => d.tradeName)
                        .join(', ')}
                    >
                      <Tag color="default" style={{ cursor: 'pointer' }}>
                        +{extra.length}
                      </Tag>
                    </Tooltip>
                  )}
                </span>
              );
            }}
          />
        )}

        <Column
          width={160}
          align="center"
          title={t('scenes.settings.roles.scene.table.columns.created_at')}
          dataIndex="createdAt"
          key="createdAt"
          render={(createdAt: Date) => (
            <span>{dayjs(createdAt).format('DD/MM/YYYY h:mm A')}</span>
          )}
        />

        <Column
          width={120}
          align="center"
          title=""
          key="actions"
          render={(row: Role) => (
            <Dropdown
              menu={{ items: getDropdownMenu(row) }}
              placement="bottomLeft"
              trigger={['click']}
            >
              <Button size="small">
                <MoreOutlined />
              </Button>
            </Dropdown>
          )}
        />
      </Table>
    </Card>
  );
}

export default RoleTable;
